import { useDispatch, useSelector } from 'react-redux'
import routes from '../../router/routes'
import useWeb3 from '../../hooks/useWeb3'
import { Box } from '../UI/Box'
import {
  BoxLeft,
  BoxRight,
  HeaderContainer,
  HeaderContentContainer,
  StickyToggleStakingCont,
  Warning,
} from './styles'
import Logo from './Logo'
import { useEffect, useMemo, useState } from 'react'
import { ThemedButton } from '../ThemedButton'
import {
  setHeaderHeight,
  setMemecoinsSearchQuery,
} from '../../modules/miscUi/actions'
import useResizeObserver from 'use-resize-observer'
import { useHistory, useLocation } from 'react-router-dom'
import { isMaintenance } from '../../page_components/Home/Vault'
import { barrySurface, useShouldShowForThreshold } from '../../theme'
import styled from 'styled-components'
import {
  BACK_BUTTON_PATH_REGEX,
  BACK_BUTTON_PATHS,
  TOKEN_DETAILS_PAGE_PATH_REGEX,
} from '../../App'
import { useHistorianNavigation } from '../../router/historian'
import WalletActiveButton from '../Library/Buttons/WalletActiveButton'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import { TopMenu } from '../BottomMenu'
import SeasonToggler from '../../page_components/Home/Season/Toggler'
import ToggleStaking from '../../page_components/Staking/ToggleStaking'
import { RoundedTextInput } from '../TextInput'
import { selectMemecoinsSearchQuery } from '../../modules/miscUi/selectors'
import { selectCurrentSeason } from '../../modules/seasons/selectors'

export const b2bUrl = process.env.REACT_APP_B2B_URL

const Header = ({ maxWidth }: { maxWidth: number }) => {
  const dispatch = useDispatch()

  const { isLoggedIn: isWalletConnected } = useIsLoggedIn()

  const { ref: headerRef, height = 1 } = useResizeObserver<HTMLDivElement>({
    box: 'border-box',
  })

  useEffect(() => {
    dispatch(setHeaderHeight(height))
  }, [dispatch, height])

  const [isScrollingDown, setIsScrollingDown] = useState(false)
  const [lastScrollY, setLastScrollY] = useState(0)
  const handleScroll = () => {
    const scrollY = window.scrollY
    if (scrollY > lastScrollY) {
      setIsScrollingDown(true)
    } else {
      setIsScrollingDown(false)
    }
    setLastScrollY(scrollY)
  }
  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [lastScrollY])

  const { pathname, search } = useLocation()

  const isBackButtonPath = useMemo(
    () =>
      BACK_BUTTON_PATHS.some((path) => pathname.includes(path)) ||
      BACK_BUTTON_PATH_REGEX.some((regex) => regex.test(pathname)) ||
      BACK_BUTTON_PATH_REGEX.some((regex) => regex.test(pathname + search)),
    [pathname, search],
  )

  const { push } = useHistory()
  const { goBack } = useHistorianNavigation()

  const handleClickBackButton = () => {
    const isTokenDetailsPage = TOKEN_DETAILS_PAGE_PATH_REGEX.some((regex) =>
      regex.test(pathname),
    )

    if (isTokenDetailsPage) {
      push(`${routes.memecoins.index}`)
      return
    }

    if (search?.includes('modal')) {
      push(pathname)
      return
    }

    goBack()
  }

  const isVaultPage = pathname === '/vault'
  const isStakingPage = pathname.includes('/staking')
  const invertColors = isVaultPage || isStakingPage

  const isAtLeastExpandedDisplaySize = useShouldShowForThreshold('medium')
  const isAtleastMediumDisplaySize = useShouldShowForThreshold('compact')

  const currentSeason = useSelector(selectCurrentSeason)

  const showSeasonToggler =
    pathname.includes('/season') &&
    isWalletConnected &&
    !isBackButtonPath &&
    !isAtLeastExpandedDisplaySize &&
    !!currentSeason

  const showMemecoinSearch = ['/', '/launch', '/launch/'].includes(pathname)

  const hideBottomBorder =
    !isAtleastMediumDisplaySize &&
    TOKEN_DETAILS_PAGE_PATH_REGEX.some((regex) => regex.test(pathname))

  return (
    <>
      {isMaintenance && (
        <Warning>
          Due to unprecedented demand, we're temporarily pausing our Vault Sale
          to scale up to expected higher throughput
        </Warning>
      )}
      <HeaderContainer
        ref={headerRef}
        maxWidth={maxWidth}
        hasBottomBorder={!hideBottomBorder}
      >
        <HeaderContentContainer maxWidth={maxWidth}>
          <BoxLeft g={32} ac fu aw>
            {showSeasonToggler ? (
              <SeasonToggler />
            ) : isBackButtonPath && !isAtLeastExpandedDisplaySize ? (
              <BackButton
                onClick={handleClickBackButton}
                bst="alpha"
                bss="enabled"
              >
                <img src="/img/icons/buttons/arrow-left.svg" alt="back" />
              </BackButton>
            ) : (
              <Logo invertColors={invertColors} />
            )}
          </BoxLeft>
          {isAtLeastExpandedDisplaySize && <TopMenu />}
          {isStakingPage && (
            <StickyToggleStakingCont isHidden={isScrollingDown}>
              <ToggleStaking />
            </StickyToggleStakingCont>
          )}
          <HeaderRight invertColors={invertColors} />
        </HeaderContentContainer>
        {showMemecoinSearch && <MemecoinsSearchInput />}
      </HeaderContainer>
    </>
  )
}

export default Header
export { RewardsAmount } from './styles'

const MemecoinsSearchInput = () => {
  const dispatch = useDispatch()
  const memecoinsSearchQuery = useSelector(selectMemecoinsSearchQuery)
  const handleOnChage = (e: React.ChangeEvent<HTMLInputElement>) => {
    // setSearchQuery(e.target.value)
    dispatch(setMemecoinsSearchQuery(e.target.value))
  }

  return (
    <SearchContainer ac jc>
      <RoundedTextInput
        value={memecoinsSearchQuery}
        onChange={handleOnChage}
        placeholder="Search for a token"
        icon={
          <img src="/img/icons/memecoins/search-token.svg" alt="search token" />
        }
      />
    </SearchContainer>
  )
}

const SearchContainer = styled(Box)`
  padding: 0px var(--Index-Grids-Gutters, 16px) 16px
    var(--Index-Grids-Gutters, 16px);
`

export function HeaderRight({
  invertColors = false,
}: {
  invertColors?: boolean
}) {
  const { initWeb3 } = useWeb3()
  const { isLoggedIn: isWalletConnected } = useIsLoggedIn()

  return (
    <BoxRight jfe ac aw fu>
      <Box ac jfe g={25} fu aw fw>
        {isWalletConnected ? (
          <WalletActiveButton />
        ) : (
          <ThemedButton
            onClick={initWeb3}
            $styleType={invertColors ? 'tertiary-invert' : 'primary'}
          >
            Connect
          </ThemedButton>
        )}
      </Box>
    </BoxRight>
  )
}

const BackButton = styled.div`
  display: flex;
  width: var(--units-40, 40px);
  height: var(--units-40, 40px);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 84px;
  border: 1px solid var(--stroke-secondary, #333);
  background: var(--surface-invert-alpha-tertiary, rgba(0, 0, 0, 0.75));
  ${barrySurface}
`
